import axios from "@/utils/axios";
import { supabase } from "@/lib/supabase";
import { useSetState } from "@/hooks/use-set-state";
import { useMemo, useEffect, useCallback, useState, useRef } from "react";
import type { Session } from "@supabase/supabase-js";

import { AuthContext } from "./auth-context";
import { AuthState } from "./auth-context-value";
import { useCurrentUser } from "@/services/user";
import {
  LINKEDIN_REVOKED_ERROR_CODE,
  LINKEDIN_REVOKED_ROUTE_CODE,
} from "@/utils/linkedin-auth-error";

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({
    user: null,
    loading: true,
  });
  const currentUserRef = useRef<AuthState["user"]>(null);

  const [sessionChecked, setSessionChecked] = useState(false);
  const [hasSession, setHasSession] = useState(false);

  const { me, loading: userLoading, error: userError } = useCurrentUser(hasSession);

  useEffect(() => {
    currentUserRef.current = state.user;
  }, [state.user]);

  const getErrorMessage = (error: unknown) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (typeof error === 'object') {
      const typed = error as { error?: string; message?: string };
      return typed.error || typed.message || '';
    }
    return '';
  };

  const shouldSignOut = (message: string) => {
    const normalized = message.toLowerCase();
    return (
      normalized.includes('user not found') ||
      normalized.includes('auth user not found') ||
      normalized.includes('authentication failed') ||
      normalized.includes('missing or invalid authorization') ||
      normalized.includes('invalid token') ||
      normalized.includes('token expired')
    );
  };

  const applySession = useCallback(
    async (session: Session | null) => {
      if (session) {
        axios.defaults.headers.common.Authorization = `Bearer ${session.access_token}`;
        const currentUser = currentUserRef.current;
        const canReuseCurrentUser = currentUser?.id === session.user.id;
        const mergedUserMetadata =
          canReuseCurrentUser && currentUser?.user_metadata && typeof currentUser.user_metadata === "object"
            ? {
                ...currentUser.user_metadata,
                ...(session.user?.user_metadata || {}),
              }
            : session.user?.user_metadata || {};

        setState({
          user: canReuseCurrentUser
            ? {
                ...currentUser,
                ...session,
                ...session.user,
                user_metadata: mergedUserMetadata,
              }
            : null,
          loading: !canReuseCurrentUser,
        });
        setHasSession(true);
        setSessionChecked(true);
        return;
      }

      setState({ user: null, loading: false });
      delete axios.defaults.headers.common.Authorization;
      setHasSession(false);
      setSessionChecked(true);
    },
    [setState]
  );

  const checkUserSession = useCallback(async () => {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        await applySession(null);
        console.error(error);
        throw error;
      }

      await applySession(session);
    } catch (error) {
      console.error(error);
      await applySession(null);
    }
  }, [applySession]);

  useEffect(() => {
    void checkUserSession();
  }, [checkUserSession]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void applySession(session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [applySession]);

  useEffect(() => {
    if (!sessionChecked || !hasSession || userLoading) {
      return;
    }

    const message = getErrorMessage(userError);

    // A revoked cohort participant is refused by the custom-access-token hook
    // during a background token refresh, where PostAuth never runs — so
    // without this they would be signed out with no explanation. The refusal
    // reason travels inside the hook's `message`, because Supabase forwards
    // only `http_code` and `message` and drops `code`.
    if (message && message.toLowerCase().includes(LINKEDIN_REVOKED_ERROR_CODE.toLowerCase())) {
      supabase.auth.signOut().finally(() => {
        delete axios.defaults.headers.common.Authorization;
        setState({ user: null, loading: false });
        setHasSession(false);
        window.location.replace(`/auth/error?code=${LINKEDIN_REVOKED_ROUTE_CODE}`);
      });
      return;
    }

    if (message && shouldSignOut(message)) {
      supabase.auth.signOut().finally(() => {
        delete axios.defaults.headers.common.Authorization;
        setState({ user: null, loading: false });
        setHasSession(false);
      });
      return;
    }

    if (userError) {
      setState({ user: null, loading: false });
      return;
    }

    if (me) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          const mergedUserMetadata = {
            ...(session.user?.user_metadata || {}),
            ...((me?.authMetadata && typeof me.authMetadata === 'object') ? me.authMetadata : {}),
          };

          setState({
            user: {
              ...session,
              ...session?.user,
              user_metadata: mergedUserMetadata,
              isSuperadmin: me?.isSuperadmin ?? false,
              hasPasswordIdentity: me?.authCapabilities?.hasPasswordIdentity ?? false,
            },
            loading: false,
          });
        } else {
          setState({ user: null, loading: false });
          setHasSession(false);
        }
      });
    } else {
      setState({ user: null, loading: false });
    }
  }, [sessionChecked, hasSession, userLoading, userError, me, setState]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

  const status = state.loading ? "loading" : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            id: state.user?.id,
            accessToken: state.user?.access_token,
            displayName: `${state.user?.user_metadata.display_name}`,
            role: state.user?.role ?? "admin",
            isSuperadmin: state.user?.isSuperadmin ?? false,
            hasPasswordIdentity: state.user?.hasPasswordIdentity ?? false,
          }
        : null,
      checkUserSession,
      loading: status === "loading",
      authenticated: status === "authenticated",
      unauthenticated: status === "unauthenticated",
    }),
    [checkUserSession, state.user, status]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
